import { useContext, useMemo } from 'react';
import { SummaryContext } from '../../../../../shared/Summary/summaryContext';
import { useSelectCurrentOrder } from '../../../../../../selectors/order/selectCurrentOrder';
import { useFindNotary } from './useFindNotary';
import { ePartyPurpose } from '../../../../../../ui-toolkit/enums/ePartyPurpose';
import { createNotaryData } from '../../../../../../ui-toolkit/orders/interfaces/IPartyPerson';
import { IParty } from '../../../../../../ui-toolkit/orders/interfaces/IParty';

const setOfSpecialParties = new Set<ePartyPurpose>([
  ePartyPurpose.notarizingAgent,
  ePartyPurpose.borrower,
  ePartyPurpose.buyer,
  ePartyPurpose.seller,
  ePartyPurpose.settlementAgent,
  ePartyPurpose.titleIssuingAgent,
  ePartyPurpose.underwriter,
  ePartyPurpose.notary,
  ePartyPurpose.lender,
  ePartyPurpose.client,
  ePartyPurpose.order_contact,
]);

export const filteredTextCallback = (filterText: string) => (party: IParty) =>
  party.purpose.toLocaleLowerCase().includes(filterText.toLocaleLowerCase());

export const useGetFilteredParties = (): IParty[] => {
  const { filterText } = useContext(SummaryContext);
  const {
    data: { parties },
  } = useSelectCurrentOrder();
  const notary = useFindNotary();

  const restrictedParties = useMemo(() => {
    const filteredParties = parties.filter((party) => party.purpose && !setOfSpecialParties.has(party.purpose));

    const underwriter = parties.find((p) => p.purpose === ePartyPurpose.underwriter);
    const titleIssuingAgent = parties.find((p) => p.purpose === ePartyPurpose.titleIssuingAgent);
    const settlementAgent = parties.find((p) => p.purpose === ePartyPurpose.settlementAgent);
    const notarizingAgent = parties.find((p) => p.purpose === ePartyPurpose.notarizingAgent);
    const notaryParty = parties.find((p) => p.purpose === ePartyPurpose.notary);
    [underwriter, titleIssuingAgent, settlementAgent, notarizingAgent, notaryParty].forEach((party) => {
      if (party) {
        filteredParties.push(party);
      }
    });

    filteredParties.push(createNotaryData(notary));

    return filteredParties;
  }, [parties, notary]);

  return useMemo(() => {
    const filterCallback = filteredTextCallback(filterText);
    return restrictedParties.filter(filterCallback);
  }, [filterText, restrictedParties]);
};
